import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getReportAuthentications } from '../services/requests';
import { useAuth } from './useAuth';
import { isAxiosError } from 'axios';
import type { ReportAuthenticationsResponse } from '../schemas/auth';

const REPORT_AUTH_CACHE_KEY = 'report_auth_cache';

const getCachedReportAuth = () => {
  try {
    const cached = localStorage.getItem(REPORT_AUTH_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.timestamp && Date.now() - parsed.timestamp < 2 * 60 * 1000) {
        return parsed.data;
      }
    }
  } catch (error) {
    console.error('Error reading report auth cache:', error);
  }
  return null;
};

const setCachedReportAuth = (data: any) => {
  try {
    localStorage.setItem(REPORT_AUTH_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (error) {
    console.error('Error writing report auth cache:', error);
  }
};

const clearCachedReportAuth = () => {
  try {
    localStorage.removeItem(REPORT_AUTH_CACHE_KEY);
  } catch (error) {
    console.error('Error clearing report auth cache:', error);
  }
};

export const useReportAuth = () => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const cachedData = useMemo(() => getCachedReportAuth(), []);

  const query = useQuery<ReportAuthenticationsResponse>({
    queryKey: ['reportAuthentications'],
    queryFn: async () => {
      const data = await getReportAuthentications();
      setCachedReportAuth(data);
      return data;
    },
    enabled: isAuthenticated && !cachedData,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...(cachedData && {
      initialData: cachedData as ReportAuthenticationsResponse,
      placeholderData: cachedData as ReportAuthenticationsResponse,
    }),
  });

  useEffect(() => {
    if (cachedData && !query.data) {
      queryClient.setQueryData(['reportAuthentications'], cachedData);
    }
  }, [cachedData, query.data, queryClient]);

  useEffect(() => {
    if (query.error && isAxiosError(query.error) && query.error.response?.status === 401) {
      clearCachedReportAuth();
      queryClient.removeQueries({ queryKey: ['reportAuthentications'] });
      queryClient.invalidateQueries({ queryKey: ['reportAuthentications'] });
    }
  }, [query.error, queryClient]);

  return query;
};
